import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';

import type { CatalogueProductView } from '../../../contracts';
import { useQueryComplimentaryUsage } from '../../../data/query/useQueryComplimentaryUsage';
import { useQueryCreditAllowance } from '../../../data/query/useQueryCreditAllowance';
import { useQueryUserBillingInfo } from '../../../data/query/useQueryUserBillingInfo';
import { useSuggestedTier } from '../../../data/query/useSuggestedTier';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import {
  COMPLIMENTARY_ENDED_VARIANTS,
  type ComplimentaryEndedTarget,
  type ComplimentaryEndedVariant,
  resolveComplimentaryEndedVariant,
} from './complimentary-ended-variants';
import {
  applyComplimentaryTrialEndedDevOverride,
  complimentaryTrialEndedDevOverrideAtom,
} from './complimentary-trial-ended-dev-override';
import { toComplimentaryEndedPlan } from './to-complimentary-ended-plan';

const toSuggestedTarget = (
  product: CatalogueProductView | null
): ComplimentaryEndedTarget =>
  product?.seat_type === BillingSeatType.MAX ? 'max' : 'clinician';

/**
 * The ended modal variant for the member's plan and usage, or the dev-tools one;
 * `null` for a plan with no variants or while the catalogue or usage that picks
 * Max loads.
 */
export function useComplimentaryTrialEndedVariant(): ComplimentaryEndedVariant | null {
  const forced = applyComplimentaryTrialEndedDevOverride(
    useAtomValue(complimentaryTrialEndedDevOverrideAtom)
  );
  const { data: user } = useQueryUser();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { data: usage } = useQueryComplimentaryUsage({ hasUser: !!user });
  const { data: allowance } = useQueryCreditAllowance({ hasUser: !!user });
  const { products } = getCommerceBillingUiPorts();
  const { data: catalogue, isLoading: isCatalogueLoading } = products.useQueryProducts({
    heidi2Catalogue: true,
  });
  const { suggestedProduct, isLoading: isSuggestionLoading } = useSuggestedTier({
    hasUser: !!user,
    products: catalogue,
    newProductTier: SubscriptionTier.CLINICIAN_PRO,
  });

  if (forced != null) return COMPLIMENTARY_ENDED_VARIANTS[forced];

  const currentPlan = toComplimentaryEndedPlan(billingInfo);
  if (currentPlan == null) return null;
  // Standard members are always sold Max, so only Free and Scribe Plus wait.
  if (currentPlan !== 'standard' && (isCatalogueLoading || isSuggestionLoading)) {
    return null;
  }

  return resolveComplimentaryEndedVariant({
    currentPlan,
    consumed: usage?.consumed,
    currentLimit: allowance?.display_limit,
    suggestedTarget: toSuggestedTarget(suggestedProduct),
  });
}
